import React, { useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import CalendlyLink from '../components/global/CalendlyLink';
import { getCalendlyUrl } from '../lib/attribution';
import { trackEvent } from '../lib/analytics';
import { CALENDLY_URL, CONTACT_CHANNELS, CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

export default function Agendar() {
  const iframeClickTracked = useRef(false);
  const calendlyEmbedUrl = useMemo(() => {
    const url = new URL(getCalendlyUrl(CALENDLY_URL, CTA_IDS.AGENDAR_PRIMARY_CALENDLY));
    url.searchParams.set('hide_event_type_details', '1');
    url.searchParams.set('hide_gdpr_banner', '1');
    return url.toString();
  }, []);

  const handleCalendlyInteraction = () => {
    if (iframeClickTracked.current) return;
    iframeClickTracked.current = true;
    trackEvent('meeting_click', {
      cta_id: CTA_IDS.AGENDAR_PRIMARY_CALENDLY,
      cta_location: CTA_LOCATIONS.PAGE_CTA,
      contact_channel: CONTACT_CHANNELS.CALENDLY,
    });
  };

  return (
    <>
      <Helmet>
        <title>Agendar una reunión | eien</title>
        <meta name="description" content="Agendá una reunión con eien para revisar tu idea, definir alcance y elegir el mejor modelo de trabajo." />
        <link rel="canonical" href="https://eientech.io/agendar" />
      </Helmet>

      <main className="min-h-screen bg-gray-50 dark:bg-[#0a0a0a] pt-28 pb-16 px-4">
        <section className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
            <div className="pt-4">
              <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">
                Sin compromiso
              </p>
              <h1 className="text-4xl md:text-5xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-5">
                Agendemos una conversación clara.
              </h1>
              <p className="text-lg text-slate-500 dark:text-slate-400 font-light leading-relaxed mb-8">
                Revisamos tu idea, entendemos el momento del negocio y te orientamos sobre alcance,
                costo y modelo de trabajo. Si no tiene sentido avanzar, también te lo decimos.
              </p>
              <CalendlyLink
                ctaId={CTA_IDS.AGENDAR_FALLBACK_CALENDLY}
                ctaLocation={CTA_LOCATIONS.PAGE_CTA}
                className="inline-flex items-center gap-2 bg-[#11b36d] text-white px-7 py-3.5 rounded-full font-semibold text-base hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
              >
                Abrir Calendly
                <span aria-hidden="true">→</span>
              </CalendlyLink>
            </div>

            <div className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-2xl overflow-hidden min-h-[760px]">
              <iframe
                title="Agenda una reunión con eien"
                src={calendlyEmbedUrl}
                className="w-full h-[760px]"
                loading="lazy"
                onPointerDown={handleCalendlyInteraction}
                onFocus={handleCalendlyInteraction}
              />
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
